"use client";

import React, { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { FaClock, FaArrowRight } from "react-icons/fa";
import { staticPosts } from "../../../lib/staticPosts";

// Define the Blog interface
interface Blog {
  ID: number;
  Title: string;
  Slug: string;
  Excerpt: string;
  CreateDate: string;
  UpdateDate?: string;
  Attachment?: string | null;
}

// Articles kept in the codebase, shown when the CMS is unavailable. Every slug is a real page.
const sampleBlogs: Blog[] = staticPosts.slice(0, 3).map((post, index) => ({ ID: -(index + 1), Title: post.title, Slug: post.slug, Excerpt: post.excerpt, CreateDate: post.date, Attachment: null }));

export default function BlogPreview() {
  const [blogs, setBlogs] = useState<Blog[]>(sampleBlogs);
  const [loading, setLoading] = useState(true);
  const imageBaseUrl = process.env.NEXT_PUBLIC_IMAGE_URL || "https://lit-000.litfusion.litpl.com";

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Fetch blogs from API
  useEffect(() => {
    async function fetchBlogs() {
      try {
        // Fetch latest 3 blogs
        const response = await fetch(`/api/blog?page=1&count=3&_t=${Date.now()}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          cache: "no-store",
        });

        if (response.ok) {
          const data: { blogs?: Blog[]; data?: Blog[] } | Blog[] = await response.json();
          
          if (process.env.NODE_ENV === "development") {
            console.log("BlogPreview API Response:", data);
          }
          
          // Handle different response formats (same as blog page)
          let blogsArray: Blog[] = [];
          
          if (Array.isArray(data)) {
            blogsArray = data;
          } else if (data.blogs && Array.isArray(data.blogs)) {
            blogsArray = data.blogs;
          } else if (data.data && Array.isArray(data.data)) {
            blogsArray = data.data;
          }
          
          if (blogsArray.length > 0) {
            setBlogs(blogsArray.slice(0, 3)); // Get only 3 latest blogs
          }
          // Keep sample blogs if API returns empty (already set in initial state)
        } else {
          if (process.env.NODE_ENV === "development") {
            console.error("Failed to fetch blogs:", response.status, response.statusText);
          }
          // Keep sample blogs on error (already set in initial state)
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching blogs:", error);
        }
        // Keep sample blogs on error (already set in initial state)
      } finally {
        setLoading(false);
      }
    }

    fetchBlogs();
  }, []);

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Latest Insights</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">Stay updated with our latest thoughts and ideas on technology, business, and innovation</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-pulse">
              <div className="h-6 w-32 bg-gray-200 rounded mb-4 mx-auto"></div>
              <div className="h-64 w-full max-w-md bg-gray-200 rounded"></div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {blogs.map((blog) => (
              <div key={blog.ID} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow border border-gray-100">
                {blog.Attachment ? (
                  <div className="relative h-48 w-full overflow-hidden bg-gray-100">
                    <Image
                      src={`${imageBaseUrl}${blog.Attachment.startsWith("/") ? blog.Attachment : `/${blog.Attachment}`}`}
                      alt={blog.Title}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      quality={75}
                      loading="lazy"
                      placeholder="blur"
                      blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-48 w-full bg-brand-100 flex items-center justify-center" aria-label="Blog placeholder">
                    <span className="text-brand-700 font-bold text-xl">LITPL Blog</span>
                  </div>
                )}

                <div className="p-6">
                  <div className="flex items-center text-gray-500 text-sm mb-3">
                    <FaClock className="mr-2" />
                    <span>{formatDate(blog.UpdateDate || blog.CreateDate)}</span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 hover:text-brand-600 transition-colors">
                    <Link href={`/blog/${blog.Slug}`}>{blog.Title}</Link>
                  </h3>

                  <p className="text-gray-600 mb-4 line-clamp-3">{blog.Excerpt || ""}</p>

                  <Link href={`/blog/${blog.Slug}`} className="text-brand-600 font-medium hover:text-brand-700 inline-flex items-center">
                    Read more
                    <FaArrowRight className="ml-2 text-sm" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Link href="/blog" className="px-6 py-3 rounded-lg border border-brand-600 text-brand-600 font-medium hover:bg-brand-50 transition-colors inline-block">
            View All Blog Posts
          </Link>
        </div>
      </div>
    </section>
  );
}
